import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/axios';

export const fetchContactPosts = createAsyncThunk("/fetchContactPosts", async ({ providerId, accountId, limit }, thunkAPI) => {
    try {
        const response = await api.post(`/api/feeds/${providerId}/posts`, {
            accountId,
            limit
        });

        return response.data.data;
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || "Failed to fetch posts!";
        return thunkAPI.rejectWithValue(errorMessage);
    }
}
);

export const fetchPostComments = createAsyncThunk("/fetchPostComments", async ({ accountId, postId }, thunkAPI) => {
    try {
        const response = await api.post("/api/feeds/post-comments",
            { accountId, postId }
        )

        return response.data.data;
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || "Failed to fetch post comments!";
        return thunkAPI.rejectWithValue(errorMessage);
    }
})

export const addReactiontoPost = createAsyncThunk("/addReactiontoPost", async ({ socialId, accountId, reactionType }, thunkAPI) => {
    try {
        const response = await api.post("/api/feeds/add-reaction",
            { socialId, accountId, reactionType }
        )

        return response.data;
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || "Failed to fetch add reaction!";
        return thunkAPI.rejectWithValue(errorMessage);
    }
})

export const removeReactionFromPost = createAsyncThunk("/removeReactionFromPost", async ({ socialId, accountId }, thunkAPI) => {
    try {
        const response = await api.post("/api/feeds/remove-reaction",
            { socialId, accountId }
        )

        return response.data;
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || "Failed to remove reaction!";
        return thunkAPI.rejectWithValue(errorMessage);
    }
})

export const commentOnPost = createAsyncThunk("/commentOnPost", async ({ socialId, accountId, text }, thunkAPI) => {
    try {
        const response = await api.post("/api/feeds/comment-on-post",
            { socialId, accountId, text }
        )

        return response.data;
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || "Failed to send comment!";
        return thunkAPI.rejectWithValue(errorMessage);
    }
})

const feedSlice = createSlice({
    name: "feedSlice",
    initialState: {
        postsByProviderId: {},
        commentsByPostId: {},
        commentsLoading: false,
        commentsError: null,
        addReaction: [],
        addReactionLoading: false,
        addReactionError: null,
        commentPostLoading: false,
        commentPostError: null,
    },
    reducers: {
        clearComments(state) {
            state.comments = [];
            state.loading = 'idle';
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetching posts
            .addCase(fetchContactPosts.pending, (state, action) => {
                const { providerId } = action.meta.arg;
                state.postsByProviderId[providerId] = {
                    ...state.postsByProviderId[providerId],
                    loading: true,
                    error: null,
                };
            })
            .addCase(fetchContactPosts.fulfilled, (state, action) => {
                const { providerId } = action.meta.arg;
                state.postsByProviderId[providerId] = {
                    data: action.payload,
                    loading: false,
                    error: null,
                };
            })
            .addCase(fetchContactPosts.rejected, (state, action) => {
                const { providerId } = action.meta.arg;
                state.postsByProviderId[providerId] = {
                    ...state.postsByProviderId[providerId],
                    loading: false,
                    error: action.payload,
                };
            })

            // Fetch post comments
            .addCase(fetchPostComments.pending, (state, action) => {
                const { postId } = action.meta.arg;
                // Set loading state for this specific post
                state.commentsByPostId[postId] = {
                    ...state.commentsByPostId[postId], // Keep existing data if any
                    loading: true,
                    error: null,
                };
            })
            .addCase(fetchPostComments.fulfilled, (state, action) => {
                const { postId } = action.meta.arg;
                // Store fetched comments under their specific postId
                state.commentsByPostId[postId] = {
                    data: action.payload,
                    loading: false,
                    error: null,
                };
            })
            .addCase(fetchPostComments.rejected, (state, action) => {
                const { postId } = action.meta.arg;
                // Set error state for this specific post
                state.commentsByPostId[postId] = {
                    ...state.commentsByPostId[postId],
                    loading: false,
                    error: action.payload,
                };
            })

            // Add reaction to post
            .addCase(addReactiontoPost.pending, (state) => {
                state.addReactionLoading = true;
                state.addReactionError = null;
            })
            .addCase(addReactiontoPost.fulfilled, (state, action) => {
                state.addReaction = action.payload;
                state.addReactionLoading = false;
            })
            .addCase(addReactiontoPost.rejected, (state, action) => {
                state.addReactionLoading = false;
                state.addReactionError = action.payload;
            })

            // Comment on post
            .addCase(commentOnPost.pending, (state) => {
                state.commentPostLoading = true;
            })
            .addCase(commentOnPost.fulfilled, (state, action) => {
                state.commentPostLoading = false;
                const { socialId, text } = action.meta.arg;

                const tempComment = {
                    id: Date.now(),
                    author: "You", // TODO: replace with logged-in user from auth slice
                    text: text,
                    date: new Date().toISOString(),
                };

                // Ensure the comment array exists before trying to add to it
                if (!state.commentsByPostId[socialId]) {
                    state.commentsByPostId[socialId] = { data: [] };
                }
                if (!state.commentsByPostId[socialId].data) {
                    state.commentsByPostId[socialId].data = [];
                }

                // Add the new comment to the correct post's comment list
                state.commentsByPostId[socialId].data.unshift(tempComment);
            })
            .addCase(commentOnPost.rejected, (state, action) => {
                state.commentPostLoading = false;
                state.commentsError = action.payload;
            });
    },
});

export const { clearComments } = feedSlice.actions;
export default feedSlice.reducer;